/**
 * Graph: the same view for every class. The object in the middle, the records its view rows link
 * to around it, each edge named by its row; the drawing scales to fit the hero. Where the class
 * has a network drawing (net-element topology, a track's neighbours), it is a second option here.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Segmented } from '@bn-apps/dim-ui-react';
import type { EntityViewModel } from '@hooks/useEntityView';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { DVIEW, pickLang, setting } from '@utils/viewShapes.utils';
import { recordName, uriText } from '../widgets/values';
import { neighbourConcept, neighbourSymbol, symbolFamily } from '../widgets/spot';
import type { WidgetContext } from '../widgets/types';
import styles from '../EntityTemplate.module.scss';

const DATA = 'https://data.banenor.no/data/';
const W = 960;
const H = 420;
// More links than this read as a list: the graph keeps the first of each relation
const MAX_NODES = 14;

interface Link {
  iri: string;
  relation: string;
  /** The class's name, so a short label such as "1" reads "Track 1". */
  className: string | null;
}

// Groups whose links are the object's own parts (geometry, dates, ids), not other records
const OWN_PARTS = ['technical', 'validity', 'identification'];

/** The named records the page's own view rows link to, one per record, in the rows' order. */
function linksOf(model: EntityViewModel, context: WidgetContext): Link[] {
  const seen = new Set<string>([model.uri]);
  const links: Link[] = [];
  for (const row of model.properties) {
    if (!row.groupId || row.hidden) continue;
    const domain = model.shapes?.groups.get(row.groupId)?.settings.domain?.[0];
    if (domain && OWN_PARTS.includes(domain)) continue;
    const relation = pickLang(row.name, context.language) ?? row.key ?? '';
    for (const value of model.values[row.id] ?? []) {
      // A record with a name of its own; nameless nodes (a length, a direction) are values, not neighbours
      if (value.type !== 'uri' || !value.value.startsWith(DATA) || !context.resolved[value.value] || seen.has(value.value)) continue;
      seen.add(value.value);
      links.push({ iri: value.value, relation, className: classLabel(model, context, row.classIri) });
    }
  }
  // Fair to every relation: round-robin over them until the limit
  const byRelation = new Map<string, Link[]>();
  for (const link of links) byRelation.set(link.relation, [...(byRelation.get(link.relation) ?? []), link]);
  const picked: Link[] = [];
  for (let round = 0; picked.length < Math.min(MAX_NODES, links.length); round++)
    for (const group of byRelation.values()) if (group[round] && picked.length < MAX_NODES) picked.push(group[round]);
  return picked.sort((a, b) => [...byRelation.keys()].indexOf(a.relation) - [...byRelation.keys()].indexOf(b.relation));
}

export const kmOf = (cells: Record<string, { value: string }[] | undefined>) => {
  const n = (key: string) => (cells[key]?.[0] ? Number(cells[key]![0].value) : NaN);
  const value = [n('track-at'), (n('track-from') + n('track-to')) / 2, n('at'), (n('from') + n('to')) / 2].find(Number.isFinite);
  return value ?? null;
};

/**
 * For an object at a km on a track (its spot row's table of what sits on that track): the previous
 * and next of its own class along the track, named "Previous signal" and "Next signal".
 */
function alongNeighbours(model: EntityViewModel, context: WidgetContext, kind: string, t: (key: string, options?: Record<string, unknown>) => string): Link[] {
  const spot = model.allProperties.find((row) => row.viewer === DVIEW.SpotViewer || row.viewer === DVIEW.SpotLocationViewer);
  const items = spot ? (model.tables[spot.id]?.items ?? []) : [];
  // Its own class (the page's first node shape) and kind, as on the strip: its TJN family, else its
  // dview:kindBy column (a signal's railML type), else its family of picture; facing the same way
  const classes = new Set(model.nodeShapes[0]?.targetClasses ?? []);
  const own = items.find((item) => item.iri === model.uri);
  const kindBy = spot ? setting(spot, 'kindBy') : null;
  const ownKind = own && kindBy ? (own.cells[kindBy]?.[0]?.value ?? null) : null;
  const ownTjn = own && spot ? (neighbourConcept(own, spot, context)?.family ?? null) : null;
  const ownFamily = own && spot ? symbolFamily(neighbourSymbol(own, spot, context)) : null;
  const ownFacing = own?.cells.facing?.[0]?.value;
  const directed = (way: string | undefined) => way === 'normal' || way === 'reverse';
  const sameKind = (item: (typeof items)[number]) =>
    (ownTjn
      ? neighbourConcept(item, spot!, context)?.family === ownTjn
      : ownKind
        ? item.cells[kindBy!]?.[0]?.value === ownKind
        : !ownFamily || symbolFamily(neighbourSymbol(item, spot!, context)) === ownFamily) &&
    (!directed(ownFacing) || !directed(item.cells.facing?.[0]?.value) || item.cells.facing?.[0]?.value === ownFacing);
  const ownKm = own ? kmOf(own.cells) : null;
  const same = items
    .filter((item) => item.iri === model.uri || (item.cells.type?.some((type) => classes.has(type.value)) && sameKind(item)))
    .map((item) => ({ iri: item.iri, km: kmOf(item.cells) }))
    .filter((item): item is { iri: string; km: number } => item.km !== null)
    // Its twins at the same km (the parts of a combined signal) are not before or after it
    .filter((item) => item.iri === model.uri || ownKm === null || Math.abs(item.km - ownKm) >= 1)
    .sort((a, b) => a.km - b.km);
  const me = same.findIndex((item) => item.iri === model.uri);
  if (me < 0) return [];
  return [
    ...(same[me - 1] ? [{ iri: same[me - 1].iri, relation: t('entity.template.graph.previous', { kind }), className: null }] : []),
    ...(same[me + 1] ? [{ iri: same[me + 1].iri, relation: t('entity.template.graph.next', { kind }), className: null }] : []),
  ];
}

/** A class's name from its view shape, else its label. */
const classLabel = (model: EntityViewModel, context: WidgetContext, iri: string | null) => {
  if (!iri) return null;
  const shape = model.shapes?.nodeShapes.find((node) => node.targetClasses.includes(iri));
  return (shape && pickLang(shape.label, context.language)) ?? context.resolved[iri]?.[0] ?? null;
};

const short = (text: string, max = 24) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

function LinkGraph({ model, context }: { model: EntityViewModel; context: WidgetContext }): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const kind = (model.nodeShapes.map((shape) => pickLang(shape.label, context.language)).find(Boolean) ?? '').toLowerCase();
  const neighbours = alongNeighbours(model, context, kind, t);
  const links = [...neighbours, ...linksOf(model, context).filter((link) => !neighbours.some((n) => n.iri === link.iri))].slice(0, MAX_NODES);
  if (links.length === 0) return <p className={styles.graphEmpty}>{t('entity.template.graph.empty')}</p>;
  const cx = W / 2;
  const cy = H / 2;
  const rx = W / 2 - 130;
  const ry = H / 2 - 50;
  const placed = links.map((link, index) => {
    const angle = -Math.PI / 2 + (index / Math.max(1, links.length)) * Math.PI * 2;
    return { ...link, x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) };
  });
  // Each relation names its edge once, on the first of its records
  const named = new Set<string>();
  return (
    <svg className={styles.graph} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={uriText(model.uri, context)}>
      {placed.map((node) => (
        <line key={`e-${node.iri}`} x1={cx} y1={cy} x2={node.x} y2={node.y} className={styles.graphEdge} />
      ))}
      {placed.map((node) => {
        if (named.has(node.relation)) return null;
        named.add(node.relation);
        return (
          <text key={`r-${node.iri}`} x={(cx + node.x) / 2} y={(cy + node.y) / 2 - 4} textAnchor="middle" className={styles.graphRelation}>
            {short(node.relation, 28)}
          </text>
        );
      })}
      {placed.map((node) => (
        <g
          key={node.iri}
          className={styles.graphNode}
          role="link"
          tabIndex={0}
          aria-label={`${node.relation}: ${recordName(node.iri, context, node.className)}`}
          onClick={() => navigate(uriToDereferencePath(node.iri))}
          onKeyDown={(event) => event.key === 'Enter' && navigate(uriToDereferencePath(node.iri))}
        >
          <title>{`${node.relation}: ${recordName(node.iri, context, node.className)}`}</title>
          <circle cx={node.x} cy={node.y} r={6} />
          <text x={node.x} y={node.y + 22} textAnchor="middle">
            {short(recordName(node.iri, context, node.className))}
          </text>
        </g>
      ))}
      <g className={styles.graphSelf}>
        <circle cx={cx} cy={cy} r={10} />
        <text x={cx} y={cy + 30} textAnchor="middle">
          {short(recordName(model.uri, context, model.nodeShapes.map((shape) => pickLang(shape.label, context.language)).find(Boolean)), 30)}
        </text>
      </g>
    </svg>
  );
}

/** The Graph tab: the links, and the class's network drawing as a second option where it has one. */
export function EntityGraph({ model, context, network }: { model: EntityViewModel; context: WidgetContext; network?: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'links' | 'network'>('links');
  return (
    <div className={styles.graphView}>
      {network && (
        <Segmented
          label={t('entity.template.graph.mode')}
          hideLabel
          size="sm"
          value={mode}
          onChange={(value) => setMode(value as 'links' | 'network')}
          options={[
            { value: 'links', label: t('entity.template.graph.links') },
            { value: 'network', label: t('entity.template.graph.network') },
          ]}
        />
      )}
      {mode === 'network' && network ? <div className={styles.graphNetwork}>{network}</div> : <LinkGraph model={model} context={context} />}
    </div>
  );
}
